import { useTranslation } from 'react-i18next';
import type { TaskIssue, TaskOccurrence } from '@momentum/types';
import { IssueStatus, RecurrencePattern } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { formatDate } from '@/shared/utils/date-utils';
import i18n from '@/i18n';
import { StopCircle, AlertTriangle, Trash2 } from 'lucide-react';
import { useUpdateTask } from '@/shared/hooks';
import { useUpdateTaskStatus } from '@/shared/hooks/useIssues';
import {
  useUpdateOccurrenceHistory,
  useDeleteOccurrence,
} from '@/shared/hooks/useTasks';
import { InlineStatusSelector } from '@/features/one-on-one/goals-review/components/InlineStatusSelector';
import { OccurrenceHistoryDrawer } from './OccurrenceHistoryDrawer';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';

interface TaskOccurrenceHistoryProps {
  task: TaskIssue;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
}

function getRecurrencePatternKey(
  task: TaskIssue
): { key: string; days?: number } | null {
  const config = task.recurrence?.config;
  if (!config) return null;

  switch (config.pattern) {
    case RecurrencePattern.DAILY:
      return { key: 'repeatsDaily' };
    case RecurrencePattern.WEEKLY:
      return { key: 'repeatsWeekly' };
    case RecurrencePattern.MONTHLY:
      return { key: 'repeatsMonthly' };
    case RecurrencePattern.CUSTOM_DAYS:
      return { key: 'repeatsCustom', days: config.intervalDays ?? 0 };
    default:
      return null;
  }
}

export const TaskOccurrenceHistory: React.FC<TaskOccurrenceHistoryProps> = ({
  task,
  isOpen,
  onClose,
  canEdit,
}) => {
  const { t } = useTranslation('goals');
  const updateTaskMutation = useUpdateTask();
  const updateOccurrenceMutation = useUpdateOccurrenceHistory();
  const deleteOccurrenceMutation = useDeleteOccurrence();
  const updateStatusMutation = useUpdateTaskStatus();
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const serverHistory = task.recurrence?.history ?? [];
  const [localHistory, setLocalHistory] = useState(serverHistory);

  useEffect(() => {
    setLocalHistory(serverHistory);
  }, [serverHistory]);

  const isClosed = !!task.metadata.closedAt;

  const handleToggleOccurrence = async (
    reversedIndex: number,
    isCompleted: boolean
  ) => {
    const actualIndex = localHistory.length - 1 - reversedIndex;
    const newValue = !isCompleted;

    setLocalHistory((prev) =>
      prev.map((occ, i) =>
        i === actualIndex ? { ...occ, isCompleted: newValue } : occ
      )
    );

    try {
      await updateOccurrenceMutation.mutateAsync({
        taskId: task._id,
        data: {
          occurrenceIndex: actualIndex,
          isCompleted: newValue,
        },
      });
      toast.success(
        newValue
          ? (t('occurrenceMarkedComplete') as string)
          : (t('occurrenceMarkedIncomplete') as string)
      );
    } catch (error) {
      console.error('Failed to update occurrence:', error);
    }
  };

  const handleDeleteOccurrence = async (reversedIndex: number) => {
    const actualIndex = localHistory.length - 1 - reversedIndex;

    setLocalHistory((prev) => prev.filter((_, i) => i !== actualIndex));

    try {
      await deleteOccurrenceMutation.mutateAsync({
        taskId: task._id,
        data: { occurrenceIndex: actualIndex },
      });
      toast.success(t('deleteOccurrenceSuccess') as string);
    } catch (error) {
      console.error('Failed to delete occurrence:', error);
    }
  };

  const handleCloseTask = async () => {
    try {
      await updateTaskMutation.mutateAsync({
        id: task._id,
        data: {
          metadata: {
            createdAt: new Date(task.metadata.createdAt),
            lastUpdatedAt: new Date(task.metadata.lastUpdatedAt),
            isDeleted: task.metadata.isDeleted,
            closedAt: new Date(),
          },
        },
      });
      setShowStopConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to close repeatable task:', error);
    }
  };

  const handleReopenTask = async () => {
    try {
      await updateTaskMutation.mutateAsync({
        id: task._id,
        data: {
          metadata: {
            createdAt: new Date(task.metadata.createdAt),
            lastUpdatedAt: new Date(task.metadata.lastUpdatedAt),
            isDeleted: task.metadata.isDeleted,
            closedAt: undefined,
          },
        },
      });
      onClose();
    } catch (error) {
      console.error('Failed to reopen repeatable task:', error);
    }
  };

  const handleCurrentStatusChange = async (newStatus: IssueStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: task._id,
        status: newStatus,
      });
      toast.success(t('occurrenceMarkedComplete') as string);
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  const patternInfo = getRecurrencePatternKey(task);
  const patternLabel = (() => {
    if (!patternInfo) return '';
    if (patternInfo.days !== undefined) {
      return (t('repeatsCustom') as (d: number) => string)(patternInfo.days);
    }
    return t(
      patternInfo.key as 'repeatsDaily' | 'repeatsWeekly' | 'repeatsMonthly'
    ) as string;
  })();

  return (
    <OccurrenceHistoryDrawer
      title={task.title}
      isOpen={isOpen}
      onClose={onClose}
      current={task.recurrence?.current}
      history={localHistory}
      headerExtra={
        patternLabel ? (
          <span className="text-xs text-muted-foreground">{patternLabel}</span>
        ) : undefined
      }
      renderCurrentOccurrence={(current) => (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('startDate')}:
            </span>
            <span className="text-sm font-medium">
              {formatDate(current.startDate, 'MMM d, yyyy', i18n.language)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('status')}:
            </span>
            <InlineStatusSelector
              value={task.status}
              onValueChange={handleCurrentStatusChange}
              disabled={!canEdit || updateStatusMutation.isPending}
            />
          </div>
        </div>
      )}
      renderPastOccurrence={(occurrence: TaskOccurrence, index: number) => (
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {t('completed')}:
          </span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={occurrence.isCompleted}
                onCheckedChange={() =>
                  handleToggleOccurrence(index, occurrence.isCompleted)
                }
                disabled={!canEdit}
              />
              <span className="text-sm">
                {occurrence.isCompleted ? t('yes') : t('no')}
              </span>
            </label>
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                onClick={() => handleDeleteOccurrence(index)}
                disabled={deleteOccurrenceMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
      footerContent={
        !canEdit ? undefined : isClosed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <StopCircle className="h-4 w-4" />
              <span>{t('recurrencePermanentlyStopped')}</span>
            </div>
            <Button
              variant="outline"
              onClick={handleReopenTask}
              disabled={updateTaskMutation.isPending}
              className="w-full"
            >
              {updateTaskMutation.isPending ? t('reopening') : t('reopenTask')}
            </Button>
          </div>
        ) : showStopConfirm ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <span className="text-sm text-destructive">
                {t('stopRecurrenceWarning')}
              </span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowStopConfirm(false)}
                disabled={updateTaskMutation.isPending}
                className="flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleCloseTask}
                disabled={updateTaskMutation.isPending}
                className="flex-1"
              >
                {updateTaskMutation.isPending
                  ? t('closing')
                  : t('stopRecurrence')}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="destructive"
            onClick={() => setShowStopConfirm(true)}
            className="w-full"
          >
            <StopCircle className="h-4 w-4" />
            {t('stopRecurrence')}
          </Button>
        )
      }
    />
  );
};
